import React from 'react'
import { Box, Button, Field, Flex, Input } from '@workwhile/ui'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  assessmentsQueueFiltersSchema,
  type AssessmentsQueueFiltersSchema,
} from '../schema'
import { useAssessmentsQueueContextValue } from '../context/AssessmentsQueueProvider'
import { CompanyPicker } from './CompanyPicker'
import { PositionPicker } from './PositionPicker'

export const AssessmentsQueueFilters = () => {
  const {
    state: { filters },
    actions: { setFilters },
  } = useAssessmentsQueueContextValue()

  const methods = useForm<AssessmentsQueueFiltersSchema>({
    resolver: zodResolver(assessmentsQueueFiltersSchema),
    reValidateMode: 'onChange',
    mode: 'all',
    defaultValues: filters,
    values: filters,
  })

  const onSubmit = (data: AssessmentsQueueFiltersSchema) => {
    setFilters(data)
  }

  return (
    <FormProvider {...methods}>
      <Flex
        as="form"
        flexDirection="row"
        flexWrap="wrap"
        gap={2}
        alignItems="flex-end"
        mb={3}
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        <Box width="256px">
          <Controller
            control={methods.control}
            name="companyId"
            render={({ field, fieldState: { error } }) => (
              <CompanyPicker
                value={field.value}
                onChange={(value) => field.onChange(value ?? null)}
                error={error?.message}
              />
            )}
          />
        </Box>
        <Box width="320px">
          <Controller
            control={methods.control}
            name="positionId"
            render={({ field, fieldState: { error } }) => (
              <PositionPicker
                value={field.value}
                onChange={(value) => field.onChange(value ?? null)}
                error={error?.message}
              />
            )}
          />
        </Box>
        {/* This Shift field is temporary while we don't build a fuzzy search endpoint to make it a Picker as well */}
        <Box width="160px">
          <Controller
            control={methods.control}
            name="shiftId"
            render={({ field, fieldState: { error } }) => (
              <Field label="Shift" error={error?.message}>
                <Input
                  block
                  type="number"
                  placeholder="Shift ID"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const next = e.target.value
                    field.onChange(next ? parseInt(next, 10) : null)
                  }}
                />
              </Field>
            )}
          />
        </Box>
        <Box mb={3}>
          <Button type="submit" variant="primary">
            Search
          </Button>
        </Box>
      </Flex>
    </FormProvider>
  )
}
